import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api.service';
import JsonView from '@uiw/react-json-view';
import { padEpicId } from '../utils/epic-id.utils';
import './EpicIdLookup.css';

interface DepartmentData {
  name: string;
  streetAddress: string;
  city: string;
  postalCode: string;
  internalId: string;
}

interface VisitTypeData {
  internalId: string;
  displayName: string;
  name: string;
  patientInstructions: string;
}

interface PeriodAttempt {
  periodOffset: number;
  dateRange: {
    start: string;
    end: string;
  };
  hasSlots: boolean;
  slotCount: number;
  status: 'checking' | 'success' | 'no-slots' | 'error';
  errorMessage?: string;
  appointmentData?: any;
}

interface LookupResult {
  attempts: PeriodAttempt[];
  hasSlots: boolean;
  canContinue: boolean;
  allAppointmentData: any[];
}

const EpicIdLookup: React.FC = () => {
  const { epicId: epicIdParam } = useParams<{ epicId?: string }>();
  const navigate = useNavigate();
  const [epicId, setEpicId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [showFullResponse, setShowFullResponse] = useState(false);
  const lookupAbortRef = useRef<AbortController | null>(null);

  useEffect(() => () => lookupAbortRef.current?.abort(), []);

  // Sorting state for Departments table
  const [deptSortColumn, setDeptSortColumn] = useState<keyof DepartmentData | null>(null);
  const [deptSortDirection, setDeptSortDirection] = useState<'asc' | 'desc'>('asc');

  // Sorting state for Appointment Summary table
  const [summarySort, setSummarySort] = useState<{ column: string; direction: 'asc' | 'desc' }>({
    column: 'date',
    direction: 'asc'
  });

  // Sorting state for VisitTypes table
  const [visitTypeSort, setVisitTypeSort] = useState<{ column: string; direction: 'asc' | 'desc' }>({
    column: 'internalId',
    direction: 'asc'
  });

  // Load Epic ID from URL on mount
  useEffect(() => {
    if (epicIdParam && epicIdParam !== epicId) {
      setEpicId(epicIdParam);
      // Clear previous results before starting new lookup
      setLookupResult(null);
      setError(null);
      // Trigger lookup automatically when Epic ID is in URL
      handleLookupWithId(epicIdParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epicIdParam]);

  const handleLookupWithId = async (idToLookup: string, continueFromPeriod?: number) => {
    if (!idToLookup.trim()) {
      setError('Please enter an Epic ID');
      return;
    }

    // Pad Epic ID with leading zeroes to minimum 5 digits
    const paddedEpicId = padEpicId(idToLookup.trim());

    // Update the displayed Epic ID with the padded version
    if (paddedEpicId !== idToLookup.trim()) {
      setEpicId(paddedEpicId);
    }

    lookupAbortRef.current?.abort();
    const abortController = new AbortController();
    lookupAbortRef.current = abortController;

    setLoading(true);
    setError(null);
    setShowFullResponse(false);

    try {
      // Determine starting period offset
      const startPeriod = continueFromPeriod !== undefined ? continueFromPeriod : 0;
      const periodsToCheck = 6; // Check 6 periods at a time
      const endPeriod = startPeriod + periodsToCheck; // Calculate end period for this batch

      // Preserve existing attempts if continuing, otherwise start fresh
      const existingAttempts = continueFromPeriod !== undefined && lookupResult ? lookupResult.attempts : [];
      const attempts: PeriodAttempt[] = [...existingAttempts];
      let allAppointmentData: any[] = continueFromPeriod !== undefined && lookupResult ? lookupResult.allAppointmentData || [] : [];
      let foundSlots = continueFromPeriod !== undefined && lookupResult ? lookupResult.hasSlots || false : false;

      // Update URL if starting a new lookup (not continuing)
      if (continueFromPeriod === undefined) {
        navigate(`/epic-lookup/${paddedEpicId}`);
      }

      // Check 6 periods at a time
      for (let periodOffset = startPeriod; periodOffset < endPeriod; periodOffset++) {
        // Calculate date range for this period
        const startDate = new Date();
        startDate.setDate(startDate.getDate() + (periodOffset * 30));
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 30);

        const startDateStr = startDate.toISOString().split('T')[0];
        const endDateStr = endDate.toISOString().split('T')[0];

        // Add attempt with "checking" status
        const currentAttempt: PeriodAttempt = {
          periodOffset,
          dateRange: { start: startDateStr, end: endDateStr },
          hasSlots: false,
          slotCount: 0,
          status: 'checking'
        };
        attempts.push(currentAttempt);

        // Update UI to show checking status
        setLookupResult({
          attempts: [...attempts],
          hasSlots: foundSlots,
          canContinue: true,
          allAppointmentData
        });

        try {
          const response = await apiService.lookupEpicId(
            paddedEpicId,
            startDateStr,
            endDateStr,
            abortController.signal
          );

          if (abortController.signal.aborted) return;

          // Count slots from response
          const scheduleDays = response.appointmentData?.ScheduleDays || [];
          const daysWithSlots = scheduleDays.filter((day: any) =>
            day.Slots && Array.isArray(day.Slots) && day.Slots.length > 0
          );
          const slotCount = daysWithSlots.length;
          const hasSlots = slotCount > 0;

          // Update the current attempt with results
          currentAttempt.hasSlots = hasSlots;
          currentAttempt.slotCount = slotCount;
          currentAttempt.status = hasSlots ? 'success' : 'no-slots';
          currentAttempt.appointmentData = response.appointmentData;

          if (hasSlots) {
            foundSlots = true;
          }

          // Always collect response data for aggregation
          allAppointmentData.push(response);

          // Update UI with completed attempt
          setLookupResult({
            attempts: [...attempts],
            hasSlots: foundSlots,
            canContinue: true,
            allAppointmentData
          });
        } catch (err: any) {
          if (err?.code === 'ERR_CANCELED') return;

          currentAttempt.status = 'error';
          currentAttempt.errorMessage = err.message || 'Failed to lookup Epic ID';

          // Update UI with error
          setLookupResult({
            attempts: [...attempts],
            hasSlots: foundSlots,
            canContinue: true,
            allAppointmentData
          });
        }
      }
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED') return;
      setError(err.message || 'Failed to lookup Epic ID');
    } finally {
      if (lookupAbortRef.current === abortController) {
        lookupAbortRef.current = null;
        if (!abortController.signal.aborted) setLoading(false);
      }
    }
  };

  const handleLookup = (continueFromPeriod?: number) => {
    handleLookupWithId(epicId, continueFromPeriod);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleLookup();
    }
  };

  // Handle sorting for Departments table
  const handleDeptSort = (column: keyof DepartmentData) => {
    if (deptSortColumn === column) {
      setDeptSortDirection(deptSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setDeptSortColumn(column);
      setDeptSortDirection('asc');
    }
  };

  // Handle sorting for Summary table
  const handleSummarySort = (column: string) => {
    if (summarySort.column === column) {
      setSummarySort({ column, direction: summarySort.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setSummarySort({ column, direction: 'asc' });
    }
  };

  // Handle sorting for VisitTypes table
  const handleVisitTypeSort = (column: string) => {
    if (visitTypeSort.column === column) {
      setVisitTypeSort({ column, direction: visitTypeSort.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setVisitTypeSort({ column, direction: 'asc' });
    }
  };

  // Decode HTML entities for rendering
  const decodeHtmlEntities = (text: string): string => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  // Get visit type display name from VisitTypes array
  const getVisitTypeDisplayName = (visitTypeId: string): string => {
    if (!lookupResult?.allAppointmentData || lookupResult.allAppointmentData.length === 0) return 'N/A';

    // Get VisitTypes from the first response (should be consistent across all responses)
    const visitTypes = lookupResult.allAppointmentData[0]?.appointmentData?.VisitTypes || [];

    // Find the visit type that has a matching ID in its IDs array
    for (const visitType of visitTypes) {
      const ids = visitType.IDs || [];
      const matchingId = ids.find((idObj: any) => idObj.ID === visitTypeId);
      if (matchingId) {
        return visitType.DisplayName || 'N/A';
      }
    }

    return 'N/A';
  };

  // Get visit type internal name from VisitTypes array
  const getVisitTypeInternalName = (visitTypeId: string): string => {
    if (!lookupResult?.allAppointmentData || lookupResult.allAppointmentData.length === 0) return 'N/A';

    // Get VisitTypes from the first response (should be consistent across all responses)
    const visitTypes = lookupResult.allAppointmentData[0]?.appointmentData?.VisitTypes || [];

    // Find the visit type that has a matching ID in its IDs array
    for (const visitType of visitTypes) {
      const ids = visitType.IDs || [];
      const matchingId = ids.find((idObj: any) => idObj.ID === visitTypeId);
      if (matchingId) {
        return visitType.Name || 'N/A';
      }
    }

    return 'N/A';
  };

  // Extract summary data from all successful attempts
  const getSummaryData = () => {
    if (!lookupResult?.allAppointmentData || lookupResult.allAppointmentData.length === 0) return [];

    const summaryMap = new Map<string, any>();

    lookupResult.allAppointmentData.forEach((response: any) => {
      const scheduleDays = response.appointmentData?.ScheduleDays || [];

      scheduleDays.forEach((day: any) => {
        if (day.Slots && Array.isArray(day.Slots) && day.Slots.length > 0) {
          const key = `${day.Date}-${day.Department?.ID}-${day.VisitType?.ID}`;

          if (summaryMap.has(key)) {
            const existing = summaryMap.get(key);
            existing.slotCount += day.Slots.length;
          } else {
            const visitTypeId = day.VisitType?.ID || 'N/A';
            summaryMap.set(key, {
              date: day.Date,
              departmentId: padEpicId(day.Department?.ID),
              visitTypeId: visitTypeId,
              visitTypeName: getVisitTypeDisplayName(visitTypeId),
              visitTypeInternalName: getVisitTypeInternalName(visitTypeId),
              slotCount: day.Slots.length
            });
          }
        }
      });
    });

    // Get array from map
    const summaryArray = Array.from(summaryMap.values());

    // Sort based on current sort settings
    return summaryArray.sort((a, b) => {
      let aVal, bVal;

      switch (summarySort.column) {
        case 'date':
          aVal = new Date(a.date).getTime();
          bVal = new Date(b.date).getTime();
          break;
        case 'departmentId':
          aVal = a.departmentId;
          bVal = b.departmentId;
          break;
        case 'visitTypeId':
          aVal = a.visitTypeId;
          bVal = b.visitTypeId;
          break;
        case 'visitTypeName':
          aVal = a.visitTypeName.toLowerCase();
          bVal = b.visitTypeName.toLowerCase();
          break;
        case 'visitTypeInternalName':
          aVal = a.visitTypeInternalName.toLowerCase();
          bVal = b.visitTypeInternalName.toLowerCase();
          break;
        case 'slotCount':
          aVal = a.slotCount;
          bVal = b.slotCount;
          break;
        default:
          aVal = new Date(a.date).getTime();
          bVal = new Date(b.date).getTime();
      }

      if (aVal < bVal) return summarySort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return summarySort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const summaryData = lookupResult ? getSummaryData() : [];
  const providerDisplayName = lookupResult?.allAppointmentData?.[0]?.appointmentData?.Provider?.DisplayName || '';

  // Extract provider metadata from IDs collection
  const providerData = lookupResult?.allAppointmentData?.[0]?.appointmentData?.Provider;
  const providerNPI = providerData?.IDs?.find((id: any) => id.Type === 'ProviderNPI')?.ID || '';
  const providerID = providerData?.IDs?.find((id: any) => id.Type === 'PROVID')?.ID || '';

  // Extract departments data from first successful response
  const getDepartmentsData = () => {
    if (!lookupResult?.allAppointmentData || lookupResult.allAppointmentData.length === 0) return [];

    const firstResponse = lookupResult.allAppointmentData[0];
    const departments = firstResponse?.appointmentData?.Departments || [];

    const departmentData = departments.map((dept: any) => {
      // Find the Internal ID from the IDs array
      const internalIdObj = dept.IDs?.find((id: any) => id.Type === 'Internal');
      const internalId = internalIdObj?.ID || 'N/A';

      return {
        name: dept.Name || 'N/A',
        streetAddress: dept.Address?.StreetAddress || 'N/A',
        city: dept.Address?.City || 'N/A',
        postalCode: dept.Address?.PostalCode || 'N/A',
        internalId: padEpicId(internalId)
      };
    });

    // Apply sorting if a column is selected
    if (deptSortColumn) {
      return departmentData.sort((a: DepartmentData, b: DepartmentData) => {
        const aVal = a[deptSortColumn];
        const bVal = b[deptSortColumn];

        if (aVal < bVal) return deptSortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return deptSortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return departmentData;
  };

  const departmentsData = lookupResult ? getDepartmentsData() : [];

  // Extract visit types data from first successful response
  const getVisitTypesData = (): VisitTypeData[] => {
    if (!lookupResult?.allAppointmentData || lookupResult.allAppointmentData.length === 0) return [];

    const firstResponse = lookupResult.allAppointmentData[0];
    const visitTypes = firstResponse?.appointmentData?.VisitTypes || [];

    // Use a Map to deduplicate by Internal ID
    const visitTypeMap = new Map<string, VisitTypeData>();

    visitTypes.forEach((vt: any) => {
      const internalIdObj = vt.IDs?.find((id: any) => id.Type === 'Internal');
      const internalId = internalIdObj?.ID || 'N/A';

      if (!visitTypeMap.has(internalId)) {
        const patientInstructions = vt.PatientInstructions || 'N/A';
        visitTypeMap.set(internalId, {
          internalId: padEpicId(internalId),
          displayName: vt.DisplayName || 'N/A',
          name: vt.Name || 'N/A',
          patientInstructions: patientInstructions !== 'N/A' ? decodeHtmlEntities(patientInstructions) : 'N/A'
        });
      }
    });

    const visitTypeData = Array.from(visitTypeMap.values());

    // Apply sorting
    return visitTypeData.sort((a: VisitTypeData, b: VisitTypeData) => {
      let aVal, bVal;

      switch (visitTypeSort.column) {
        case 'internalId':
          aVal = a.internalId;
          bVal = b.internalId;
          break;
        case 'displayName':
          aVal = a.displayName;
          bVal = b.displayName;
          break;
        case 'name':
          aVal = a.name;
          bVal = b.name;
          break;
        case 'patientInstructions':
          aVal = a.patientInstructions;
          bVal = b.patientInstructions;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return visitTypeSort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return visitTypeSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const visitTypesData = lookupResult ? getVisitTypesData() : [];

  // Get the first successful response for JSON viewer
  const apiResponse = lookupResult?.allAppointmentData?.[0] || null;

  return (
    <div className="epic-lookup-container">
      <div className="epic-lookup-card">
        <h2>Epic ID Lookup</h2>
        <p className="epic-lookup-description">
          Look up appointment availability for a specific Epic ID using a LIVE call to Epic. This will return all visible Departments and Visit Types for the provider.
        </p>

        <div className="epic-lookup-form">
          <div className="form-group">
            <label htmlFor="epicId">Epic ID</label>
            <input
              type="text"
              id="epicId"
              value={epicId}
              onChange={(e) => setEpicId(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter Epic ID (e.g., 138532)"
              disabled={loading}
            />
          </div>

          <button
            onClick={() => handleLookup()}
            disabled={loading || !epicId.trim()}
            className="btn btn-primary"
          >
            {loading ? 'Looking up...' : 'Lookup'}
          </button>
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {lookupResult && (
          <div className="api-response-container">
            <div className="response-header">
              <h3>Lookup Results</h3>
              <button
                onClick={() => {
                  lookupAbortRef.current?.abort();
                  lookupAbortRef.current = null;
                  setLoading(false);
                  setShowFullResponse(false);
                  setLookupResult(null);
                  navigate('/epic-lookup');
                }}
                className="btn btn-sm btn-secondary"
              >
                Clear
              </button>
            </div>

            {/* Display all period attempts */}
            {lookupResult.attempts.length > 0 && (
              <div className="lookup-attempts">
                <h4>Checked Periods</h4>
                <table className="validation-attempts-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Date Range</th>
                      <th>Status</th>
                      <th>Slots Found</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lookupResult.attempts.map((attempt, idx) => (
                      <tr key={idx} className={`attempt-${attempt.status}`}>
                        <td>
                          {attempt.periodOffset === 0 ? 'Next 30 days' : `Days ${attempt.periodOffset * 30}-${(attempt.periodOffset + 1) * 30}`}
                        </td>
                        <td>
                          {attempt.dateRange.start} to {attempt.dateRange.end}
                        </td>
                        <td>
                          {attempt.status === 'checking' && <span className="status-badge checking">Checking...</span>}
                          {attempt.status === 'success' && <span className="status-badge success">✓ Found</span>}
                          {attempt.status === 'no-slots' && <span className="status-badge no-slots">No Slots</span>}
                          {attempt.status === 'error' && <span className="status-badge error">Error</span>}
                        </td>
                        <td className="slots-count">
                          {attempt.status === 'success' ? attempt.slotCount : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {lookupResult.attempts.some(a => a.status === 'error') && (
                  <div className="error-message" style={{ marginTop: '0.5rem' }}>
                    {lookupResult.attempts.find(a => a.errorMessage)?.errorMessage}
                  </div>
                )}
              </div>
            )}

            {/* Continue button to check more periods */}
            {!loading && lookupResult.canContinue && lookupResult.attempts.length >= 6 && (
              <div className="validation-actions" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
                <button
                  onClick={() => {
                    const lastPeriod = lookupResult.attempts[lookupResult.attempts.length - 1]?.periodOffset || 0;
                    handleLookup(lastPeriod + 1);
                  }}
                  disabled={loading}
                  className="btn btn-secondary btn-sm"
                >
                  Check Next 6 Months
                </button>
                <span className="validation-hint">
                  Continue pulling data for another 6 months
                </span>
              </div>
            )}

            {/* Provider Information */}
            {providerDisplayName && (
              <div className="provider-info-container">
                <div className="provider-info-box">
                  <span className="provider-info-label">Provider</span>
                  <span className="provider-info-value">{providerDisplayName}</span>
                </div>
                {providerNPI && (
                  <div className="provider-info-box">
                    <span className="provider-info-label">NPI</span>
                    <span className="provider-info-value">{providerNPI}</span>
                  </div>
                )}
                {providerID && (
                  <div className="provider-info-box">
                    <span className="provider-info-label">PROVID</span>
                    <span className="provider-info-value">{providerID}</span>
                  </div>
                )}
              </div>
            )}

            {/* Departments Table */}
            {departmentsData.length > 0 && (
              <div className="summary-section">
                <h4>Departments</h4>
                <table className="departments-table sortable">
                  <thead>
                    <tr>
                      <th onClick={() => handleDeptSort('name')} className="sortable-header">
                        Name {deptSortColumn === 'name' && (deptSortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleDeptSort('streetAddress')} className="sortable-header">
                        Street Address {deptSortColumn === 'streetAddress' && (deptSortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleDeptSort('city')} className="sortable-header">
                        City {deptSortColumn === 'city' && (deptSortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleDeptSort('postalCode')} className="sortable-header">
                        Postal Code {deptSortColumn === 'postalCode' && (deptSortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleDeptSort('internalId')} className="sortable-header">
                        Dept ID {deptSortColumn === 'internalId' && (deptSortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {departmentsData.map((dept: DepartmentData, idx: number) => (
                      <tr key={idx}>
                        <td>{dept.name}</td>
                        <td>{dept.streetAddress}</td>
                        <td>{dept.city}</td>
                        <td>{dept.postalCode}</td>
                        <td>{dept.internalId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Visit Types Table */}
            {visitTypesData.length > 0 && (
              <div className="summary-section">
                <h4>Visit Types</h4>
                <p className="section-description">
                  Shows the visit types that are visible to the Epic API. Not all visit types will have appointment slots, but this will give you an idea of what is available to be pulled.
                </p>
                <table className="validation-results-table sortable">
                  <thead>
                    <tr>
                      <th onClick={() => handleVisitTypeSort('internalId')} className="sortable-header">
                        Internal ID {visitTypeSort.column === 'internalId' && (visitTypeSort.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleVisitTypeSort('displayName')} className="sortable-header">
                        Display Name {visitTypeSort.column === 'displayName' && (visitTypeSort.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleVisitTypeSort('name')} className="sortable-header">
                        Name {visitTypeSort.column === 'name' && (visitTypeSort.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleVisitTypeSort('patientInstructions')} className="sortable-header">
                        Patient Instructions {visitTypeSort.column === 'patientInstructions' && (visitTypeSort.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visitTypesData.map((vt: VisitTypeData, idx: number) => (
                      <tr key={idx}>
                        <td>{vt.internalId}</td>
                        <td>{vt.displayName}</td>
                        <td>{vt.name}</td>
                        <td
                          className="text-left"
                          dangerouslySetInnerHTML={{ __html: vt.patientInstructions !== 'N/A' ? vt.patientInstructions : 'N/A' }}
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Summary Table */}
            {summaryData.length > 0 && (
              <div className="summary-section">
                <h4>Appointment Summary</h4>
                <div className="scrollable-table-container">
                  <table className="validation-results-table sortable">
                    <thead>
                      <tr>
                        <th onClick={() => handleSummarySort('date')} className="sortable-header">
                          Date {summarySort.column === 'date' && (summarySort.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleSummarySort('departmentId')} className="sortable-header">
                          Department ID {summarySort.column === 'departmentId' && (summarySort.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleSummarySort('visitTypeId')} className="sortable-header">
                          Visit Type ID {summarySort.column === 'visitTypeId' && (summarySort.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleSummarySort('visitTypeName')} className="sortable-header">
                          VT-Name {summarySort.column === 'visitTypeName' && (summarySort.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleSummarySort('visitTypeInternalName')} className="sortable-header">
                          VT-Internal Name {summarySort.column === 'visitTypeInternalName' && (summarySort.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleSummarySort('slotCount')} className="sortable-header">
                          Slot Count {summarySort.column === 'slotCount' && (summarySort.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryData.map((slot, idx) => (
                        <tr key={idx}>
                          <td>{slot.date}</td>
                          <td>{slot.departmentId}</td>
                          <td>{slot.visitTypeId}</td>
                          <td>{slot.visitTypeName}</td>
                          <td>{slot.visitTypeInternalName}</td>
                          <td>{slot.slotCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Full JSON Response */}
            {apiResponse && (
              <div className="full-response-section">
                <h4>Full API Response</h4>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowFullResponse(current => !current)}
                >
                  {showFullResponse ? 'Hide Full Response' : 'Show Full Response'}
                </button>
                {showFullResponse && (
                  <div className="json-viewer-wrapper">
                    <JsonView value={apiResponse} collapsed={2} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EpicIdLookup;
